/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { ReactElement } from 'react'

import { EuiButton, EuiToolTip } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { DeploymentGetResponse } from '@modules/cloud-api/v1/types'
import type { AnyUserProject } from '@modules/project-user-api/types'
import usePermissions from '@modules/permissions-lib/hooks'

interface CommonProps {
  isLoading: boolean
  isSubmittable: boolean
  onClick: () => void
}

interface BaseApplyButtonProps extends CommonProps {
  hasPermissions: boolean
}

interface ProjectApplyButtonProps extends CommonProps {
  project: AnyUserProject
}

interface DeploymentApplyButtonProps extends CommonProps {
  deployment: DeploymentGetResponse
}

const NoPermissionTooltip = ({ children }: { children: ReactElement }) => (
  <EuiToolTip
    content={
      <FormattedMessage
        id='trafficFilterAssociationForm.noPermission'
        defaultMessage='You do not have permission to apply this traffic filter.'
      />
    }
  >
    {children}
  </EuiToolTip>
)

const BaseApplyButton = (props: BaseApplyButtonProps) => {
  const { isLoading, isSubmittable, hasPermissions, onClick } = props

  const canSubmit = isSubmittable && hasPermissions

  const button = (
    <EuiButton
      fill={true}
      isLoading={isLoading}
      disabled={!canSubmit}
      onClick={onClick}
      data-test-id='apply-association-button'
      data-test-subj='trafficFilterAssociationForm.applyButton'
    >
      <FormattedMessage id='trafficFilterAssociationForm.apply' defaultMessage='Apply' />
    </EuiButton>
  )

  if (hasPermissions || isLoading) {
    return button
  }

  return <NoPermissionTooltip>{button}</NoPermissionTooltip>
}

const ProjectApplyButton = (props: ProjectApplyButtonProps) => {
  const { project, isLoading, isSubmittable, onClick } = props
  const { type, id } = project

  const { hasPermissions, isLoading: isLoadingUpdatePermission } = usePermissions([
    { type: `project-${type}`, action: 'update', id },
  ])

  return (
    <BaseApplyButton
      isLoading={isLoading || isLoadingUpdatePermission}
      isSubmittable={isSubmittable}
      hasPermissions={hasPermissions}
      onClick={onClick}
    />
  )
}

const DeploymentApplyButton = (props: DeploymentApplyButtonProps) => {
  const { deployment, isLoading, isSubmittable, onClick } = props

  const { hasPermissions, isLoading: isLoadingUpdatePermission } = usePermissions([
    { type: `deployment`, action: 'update', id: deployment.id },
  ])

  return (
    <BaseApplyButton
      isLoading={isLoading || isLoadingUpdatePermission}
      isSubmittable={isSubmittable}
      hasPermissions={hasPermissions}
      onClick={onClick}
    />
  )
}

interface ApplyAssociationButtonProps extends CommonProps {
  deployment?: DeploymentGetResponse
  project?: AnyUserProject
}

export const ApplyAssociationButton = (props: ApplyAssociationButtonProps) => {
  const { deployment, project, ...commonProps } = props

  if (deployment) {
    return <DeploymentApplyButton deployment={deployment} {...commonProps} />
  }

  if (project) {
    return <ProjectApplyButton project={project} {...commonProps} />
  }

  return null
}
